/**
 * The agent-authored script behind an automation.
 *
 * One source: the record's `ahoy_script` property. On a live record that is
 * the SAVED script — what it runs today. Under a pending proposal the draft's
 * script is projected onto the object like any other proposed property, so
 * the same read shows what the automation WILL run if saved. Read `all` as of
 * the slideout redesign; built-ins carry no script at all (their logic is a
 * registered module), so this self-gates to nothing for them.
 *
 * A historical correction: this section used to claim `ahoy_script` was
 * unreadable client-side and the proposal payload was "the only place a client
 * can read it". That was never true of the dashboard REST payload — the
 * record-detail GET has always returned every property; the frontend just
 * discarded non-`all` ones client-side. The schema now says `all`, matching
 * what the server always did.
 */

import { CodeBlock } from '@/lib/base/code';
import type { CrmObject } from '@/types/crm';

import {
  cleanScriptForDisplay,
  getEffectiveScriptSource,
} from '../utils/scriptSource';
import { CollapsibleSection } from './collapsible-section';

interface DraftScriptSectionProps {
  /** The record — saved, or projected from a pending proposal. */
  object: CrmObject;
}

export function DraftScriptSection({ object }: DraftScriptSectionProps) {
  const effective = getEffectiveScriptSource(object);

  if (!effective) {
    return null;
  }

  return (
    <CollapsibleSection title='Script' defaultExpanded={false}>
      {/* Same cleaning as the Code tab — imports and the run() signature are
          contract boilerplate; only the body says what this automation does. */}
      <CodeBlock source={cleanScriptForDisplay(effective)} />
    </CollapsibleSection>
  );
}
